<template>
	<Teleport to="body">
		<div v-if="isOpen" class="modal-overlay" @click="emit('close')">
			<div class="modal-content" @click.stop>
				<div class="modal-header">
					<div class="titles">
						<h3>{{ $t("opportunities.transferModal.title") }}</h3>
						<p class="subtitle">{{ $t("opportunities.transferModal.subtitle") }}</p>
						<p v-if="fromProjectName" class="from-project">
							{{ $t("opportunities.transferModal.from", { project: fromProjectName }) }}
						</p>
					</div>
					<button type="button" class="close-btn" @click="emit('close')">&times;</button>
				</div>

				<div class="search">
					<div class="field">
						<AppIcon :name="IconName.Search" class="icon" />
						<input
							v-model="searchQuery"
							type="text"
							:placeholder="$t('project-switcher.search-placeholder')"
							autofocus
						/>
					</div>
				</div>

				<div class="list">
					<button
						v-for="project in filteredProjects"
						:key="project.value"
						type="button"
						class="item"
						@click="handleSelect(project.value)"
					>
						<span class="folder-icon">
							<AppIcon :name="IconName.Folder" />
						</span>
						<span class="name">{{ project.label }}</span>
					</button>

					<p v-if="filteredProjects.length === 0" class="empty">{{ $t("project-switcher.no-results") }}</p>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"

interface TransferProjectOption {
	value: string
	label: string
}

const props = defineProps<{
	isOpen: boolean
	projects: TransferProjectOption[]
	fromProjectName?: string
}>()

const emit = defineEmits<{
	(e: "close"): void
	(e: "select", value: string): void
}>()

const searchQuery = ref("")

watch(
	() => props.isOpen,
	isOpen => {
		if (isOpen) searchQuery.value = ""
	}
)

const filteredProjects = computed(() => {
	const query = searchQuery.value.trim().toLowerCase()
	if (!query) return props.projects
	return props.projects.filter(project => project.label.toLowerCase().includes(query))
})

function handleSelect(value: string): void {
	emit("select", value)
	emit("close")
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
	backdrop-filter: blur(4px);
}

.modal-content {
	background: var(--neutral-0);
	border-radius: var(--border-radius-lg);
	width: 90%;
	max-width: 480px;
	max-height: 60vh;
	display: flex;
	flex-direction: column;
	box-shadow: var(--shadow-xl);
	overflow: hidden;
	padding: var(--spacing-lg);
	gap: var(--spacing-md);
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: var(--spacing-sm);

	.titles {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
	}

	h3 {
		margin: 0;
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-semibold);
	}

	.subtitle {
		margin: 0;
		color: var(--neutral-600);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-regular);
	}

	.from-project {
		margin: 0;
		color: var(--neutral-600);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-medium);
	}

	.close-btn {
		flex-shrink: 0;
		border: none;
		background: none;
		font-size: var(--font-size-xl);
		line-height: 1;
		color: var(--neutral-600);
		cursor: pointer;

		&:hover {
			color: var(--neutral-900);
		}
	}
}

.search {
	.field {
		position: relative;
		display: flex;
		align-items: center;
	}

	.icon {
		position: absolute;
		left: var(--spacing-sm);
		width: var(--size-icon-md);
		height: var(--size-icon-md);
		color: var(--neutral-600);
		pointer-events: none;
	}

	input {
		width: 100%;
		padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm) var(--spacing-xl);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-md);
		background-color: var(--neutral-50);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-regular);
		outline: none;

		&:focus {
			border-color: var(--neutral-400);
			background-color: var(--neutral-0);
		}
	}
}

.list {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	max-height: 320px;
	overflow-y: auto;

	.item {
		display: flex;
		align-items: center;
		gap: var(--spacing-md);
		width: 100%;
		padding: var(--spacing-md);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-lg);
		background: var(--neutral-0);
		cursor: pointer;
		text-align: left;
		transition: border-color 0.15s ease;

		&:hover {
			border-color: var(--neutral-400);
			background-color: var(--neutral-50);
		}

		.folder-icon {
			flex-shrink: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			width: var(--size-icon-xl);
			height: var(--size-icon-xl);
			border-radius: var(--border-radius-md);
			background: var(--neutral-100);

			.app-icon {
				width: var(--size-icon-md);
				height: var(--size-icon-md);
				color: var(--neutral-600);
			}
		}

		.name {
			flex: 1;
			min-width: 0;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-medium);
		}
	}

	.empty {
		padding: var(--spacing-md);
		text-align: center;
		color: var(--neutral-600);
		font-style: italic;
	}
}
</style>
